import { useEffect, useState } from "react";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import {
  Plus,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Search,
  Settings2
} from "lucide-react";

import { MachineCard } from "./components/machine-card";
import { AddMachineSheet } from "./components/add-machine-sheet";
import { EditMachineSheet } from "./components/edit-machine-sheet";
import { useMachines } from "@/hooks/use-machines";
import { WorkspaceToggle } from "@/components/workspace-toggle";
import { useRefetchStore } from "@/stores/store";
import { PLANNER_PERMISSIONS } from "@/constants/permission";
import { useUserProfileStore } from "@/stores/user-profile-store";

export const Route = createFileRoute("/plant-3/machines/")({
  component: RouteComponent,
  beforeLoad: () => {
    const { userProfile } = useUserProfileStore.getState();
    if (userProfile?.rolePermissions) {
      const hasPermission = userProfile.rolePermissions.includes(
        PLANNER_PERMISSIONS.MACHINE_MANAGE
      );
      if (!hasPermission) {
        throw redirect({ to: "/plant-3/list" });
      }
    }
  }
});

function RouteComponent() {
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);
  const [showAddSheet, setShowAddSheet] = useState(false);
  const [showEditSheet, setShowEditSheet] = useState(false);
  const [editMachineId, setEditMachineId] = useState<string | null>(null);

  const {
    allMachines,
    filteredShops,
    nonOperationalMachines,
    searchQuery,
    setSearchQuery,
    clearSearch,
    refetch,
    collapsedShops,
    toggleShopCollapse,
    total,
    totalPages
  } = useMachines({
    page,
    pageSize: pageSize
  });

  // Reset to page 1 when pageSize or search changes
  const handlePageSizeChange = (value: string) => {
    setPage(1);
    setPageSize(Number(value));
  };

  const handleSearchChange = (value: string) => {
    setPage(1);
    setSearchQuery(value);
  };

  const { setRefetch } = useRefetchStore();

  useEffect(() => {
    setRefetch(refetch);
  }, [refetch]);

  const handleEditClick = (machineId: string) => {
    setEditMachineId(machineId);
    setShowEditSheet(true);
  };

  const handleEditClose = (open: boolean) => {
    setShowEditSheet(open);
    if (!open) {
      setEditMachineId(null);
    }
  };

  const handleEditSuccess = () => {
    setShowEditSheet(false);
    setEditMachineId(null);
  };

  return (
    <div className="aero:flex aero:flex-col aero:gap-3 aero:py-6 aero:md:py-10 aero:w-full aero:max-w-full aero:overflow-hidden">
      {/* Page Header */}
      <div className="aero:flex aero:items-center aero:justify-between aero:min-h-10">
        <div className="aero:flex aero:items-center aero:gap-3">
          <div className="aero:w-10 aero:h-10 aero:rounded-lg aero:bg-brand-primary-50 aero:flex aero:items-center aero:justify-center">
            <Settings2 className="aero:text-brand-primary-500 aero:h-[27px] aero:w-[27px] aero:p-0.5" />
          </div>
          <div>
            <h1 className="aero:text-xl aero:md:text-2xl aero:text-foreground aero:font-bold aero:leading-9">
              Machines
            </h1>
            <p className="aero:text-xs aero:md:text-sm aero:text-neutral-500 aero:leading-5">
              Monitor and manage machine status across all shops
            </p>
          </div>
        </div>
        <WorkspaceToggle />
      </div>
      {/* Search and Filter Bar */}
      <Card className="aero:!rounded-lg aero:!border-neutral-200 aero:!p-4 aero:!gap-0 aero:!shadow-none">
        <div className="aero:flex aero:items-center aero:justify-between aero:gap-4">
          <div className="aero:relative aero:flex-1 aero:max-w-md">
            <Search className="aero:absolute aero:left-3 aero:top-1/2 aero:transform aero:-translate-y-1/2 aero:h-4 aero:w-4 aero:text-neutral-500" />
            <Input
              placeholder="Search machines by machine name or technician..."
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="aero:pl-10 aero:h-9"
            />
          </div>

          <div className="aero:flex aero:items-center aero:gap-2">
            <span className="aero:text-sm aero:text-neutral-600 aero:whitespace-nowrap">
              Show:
            </span>
            <Select
              value={pageSize.toString()}
              onValueChange={handlePageSizeChange}
            >
              <SelectTrigger className="aero:w-20 aero:h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>

            <Button
              type="button"
              intent="primary"
              onClick={() => setShowAddSheet(!showAddSheet)}
              className="aero:h-9 aero:gap-2 aero:rounded aero:px-3 aero:py-2"
            >
              <Plus className="aero:h-4 aero:w-4" />
              Add Machine
            </Button>
          </div>
        </div>
      </Card>
      {/* Non-operational Machines Alert */}
      {nonOperationalMachines.length > 0 && (
        <Card className="aero:!rounded-lg aero:!border-red-200 aero:!bg-red-50 aero:!p-4 aero:!gap-0 aero:!shadow-none">
          <div className="aero:flex aero:items-center aero:gap-2 aero:mb-3">
            <AlertTriangle className="aero:h-5 aero:w-5 aero:text-red-600" />
            <h2 className="aero:text-base aero:font-semibold aero:text-red-900">
              Machines Requiring Attention ({nonOperationalMachines.length})
            </h2>
          </div>

          <div className="aero:grid aero:grid-cols-1 aero:sm:grid-cols-3 aero:xxl:grid-cols-4 aero:gap-2">
            {nonOperationalMachines.map((machine) => (
              <MachineCard
                key={machine.id}
                machine={machine}
                onEdit={handleEditClick}
              />
            ))}
          </div>
        </Card>
      )}
      {/* Machines by Shop */}
      <Card className="aero:!rounded-lg aero:!border-neutral-200 aero:!p-0 aero:!gap-0 aero:!shadow-none aero:min-w-0">
        <CardContent className="aero:!p-4 aero:min-w-0">
          <div className="aero:space-y-4">
            {filteredShops.map((shop) => {
              const isCollapsed = collapsedShops.has(shop?.shopId?.toString());

              return (
                <div key={shop.shopId} className="aero:space-y-2">
                  <button
                    onClick={() => toggleShopCollapse(shop.shopId)}
                    className="aero:flex aero:items-center aero:gap-2 aero:w-full aero:text-left aero:hover:bg-neutral-50 aero:rounded-lg aero:p-2 aero:-m-2 aero:transition-colors aero:cursor-pointer"
                  >
                    {isCollapsed ? (
                      <ChevronRight className="aero:h-5 aero:w-5 aero:text-neutral-500" />
                    ) : (
                      <ChevronDown className="aero:h-5 aero:w-5 aero:text-neutral-500" />
                    )}
                    <h2 className="aero:text-base aero:font-semibold aero:text-foreground">
                      {shop?.shopName}
                    </h2>
                    <span className="aero:text-xs aero:font-normal aero:text-neutral-500">
                      {shop.machineCount < shop.totalMachineCount
                        ? `(${shop.machineCount} of ${shop.totalMachineCount} machines on this page)`
                        : `(${shop.machines.length} machines)`}
                    </span>
                    <div className="aero:ml-auto aero:flex aero:gap-2">
                      <Badge
                        variant="outline"
                        className="aero:text-green-700 aero:bg-green-50 aero:border-green-200"
                      >
                        {shop.availableCount} Available
                      </Badge>
                      {shop.maintenanceCount > 0 && (
                        <Badge
                          variant="outline"
                          className="aero:text-yellow-700 aero:bg-yellow-50 aero:border-yellow-200"
                        >
                          {shop.maintenanceCount} Maintenance
                        </Badge>
                      )}
                      {shop.unavailableCount > 0 && (
                        <Badge
                          variant="outline"
                          className="aero:text-red-700 aero:bg-red-50 aero:border-red-200"
                        >
                          {shop.unavailableCount} Unavailable
                        </Badge>
                      )}
                    </div>
                  </button>

                  {!isCollapsed && (
                    <div className="aero:grid aero:grid-cols-1 aero:sm:grid-cols-2 aero:lg:grid-cols-3 aero:xxl:grid-cols-4 aero:gap-2">
                      {shop.machines.map((machine: any) => (
                        <MachineCard
                          key={machine.id}
                          machine={machine}
                          onEdit={handleEditClick}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Empty State */}
          {allMachines.length === 0 && (
            <div className="aero:text-center aero:py-12">
              <div className="aero:w-16 aero:h-16 aero:mx-auto aero:mb-4 aero:rounded-full aero:bg-neutral-100 aero:flex aero:items-center aero:justify-center">
                {searchQuery ? (
                  <Search className="aero:h-8 aero:w-8 aero:text-neutral-400" />
                ) : (
                  <Settings2 className="aero:h-8 aero:w-8 aero:text-neutral-400" />
                )}
              </div>
              <h3 className="aero:text-lg aero:font-medium aero:mb-2 aero:text-foreground">
                No machines found
              </h3>
              <p className="aero:text-sm aero:text-neutral-500 aero:mb-4">
                {searchQuery
                  ? `No machines match your search for "${searchQuery}"`
                  : "Get started by adding your first machine to the system."}
              </p>
              {searchQuery ? (
                <Button
                  onClick={clearSearch}
                  intent="secondary"
                  design="outline"
                  className="aero:h-9 aero:rounded aero:px-3 aero:py-2"
                >
                  Clear Search
                </Button>
              ) : (
                <Button
                  intent="primary"
                  onClick={() => setShowAddSheet(!showAddSheet)}
                  className="aero:h-9 aero:gap-2 aero:rounded aero:px-3 aero:py-2"
                >
                  <Plus className="aero:h-4 aero:w-4" />
                  Add Your First Machine
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="aero:flex aero:items-center aero:justify-between aero:px-1">
          <span className="aero:text-sm aero:text-neutral-500">
            Showing {(page - 1) * pageSize + 1}–
            {Math.min(page * pageSize, total)} of {total} machines
          </span>
          <div className="aero:flex aero:items-center aero:gap-2">
            <Button
              type="button"
              intent="neutral"
              design="outline"
              className="aero:h-8 aero:px-3"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <span className="aero:text-sm aero:text-neutral-600">
              Page {page} of {totalPages}
            </span>
            <Button
              type="button"
              intent="neutral"
              design="outline"
              className="aero:h-8 aero:px-3"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </Button>
          </div>
        </div>
      )}
      {/* Add Machine Sheet */}
      <AddMachineSheet open={showAddSheet} onOpenChange={setShowAddSheet} />
      {/* Edit Machine Sheet */}
      <EditMachineSheet
        open={showEditSheet}
        onOpenChange={handleEditClose}
        machineId={editMachineId || ""}
        onSuccess={handleEditSuccess}
      />
    </div>
  );
}
